import React, { useEffect, useRef, useState } from 'react';
import { X, Mic, Users, ChevronDown, ShieldCheck, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocalizationStore } from '@/stores/useLocalizationStore';
import { useTranslation } from '@/hooks/useTranslation';
import { useRecordingSessionStore } from '@/stores/useRecordingSessionStore';
import { useVoiceStore } from '@/stores/useVoiceStore';
import { audioRetentionApi } from '@/services/audioRetentionApi';
import { PatientDisclosureModal } from '@/components/audioRetention/PatientDisclosureCard';
import type { Template } from '@/services/api/templatesApi';
import { TemplatePickerPhase } from './TemplatePickerPhase';
import { rememberRecentTemplate } from './templateRecents';
import { useDefaultTemplate } from '@/hooks/useDefaultTemplate';

type CaptureMode = 'visit' | 'dictate';
type SttLang = 'en' | 'ar' | 'auto';

/**
 * Globally-mounted, store-driven "arm" panel for starting an encounter.
 *
 * Renders whenever `armIntent` is set (via `armSession()` from ANY tap-to-
 * record entry point — the DoctorHome mic FAB, a schedule tile, the launcher
 * pill, the patient page). The mic stays COLD until the doctor taps "Start
 * recording", which fixes quick-starts going hot instantly and capturing the
 * room. Everything runs off the doctor's saved defaults (capture mode +
 * language + specialty template), shown as a plain summary; "Change" reveals
 * per-encounter overrides that never touch the saved defaults.
 *
 * Patient is optional: an unassigned start (mic FAB / walk-in) just omits the
 * patient row — the dock's "attach a patient" affordance covers it later.
 */
export const EncounterArmPanel: React.FC = () => {
  const {
    config: { language: uiLang },
  } = useLocalizationStore();
  const { t } = useTranslation();
  const armIntent = useRecordingSessionStore((s) => s.armIntent);
  const startSession = useRecordingSessionStore((s) => s.startSession);
  const cancelArm = useRecordingSessionStore((s) => s.cancelArm);
  const defaultCaptureMode = useVoiceStore(
    (s) => s.settings.defaultCaptureMode,
  );
  const defaultLanguage = useVoiceStore((s) => s.settings.sttLanguage);

  const isRtl = uiLang === 'ar';
  const open = !!armIntent;

  const [captureMode, setCaptureMode] = useState<CaptureMode>('visit');
  const [lang, setLang] = useState<SttLang>('en');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null,
  );
  const [showChange, setShowChange] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const startedRef = useRef(false);
  // Consent: only relevant when a PATIENT is attached AND AI-training
  // retention is on for this doctor. Dictation with no patient never needs
  // it (nobody to consent). Folded inline so Start doubles as the attestation
  // — no separate modal.
  const [needsConsent, setNeedsConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [disclosureOpen, setDisclosureOpen] = useState(false);

  const defaultTemplate = useDefaultTemplate();

  // Re-seed the per-encounter selections from the doctor's saved defaults
  // each time the panel opens (a fresh intent).
  useEffect(() => {
    if (!armIntent) return;
    startedRef.current = false;
    // Honor a mode the entry point pre-set (e.g. a "dictate note" quick
    // action); otherwise the doctor's saved default.
    setCaptureMode(
      (armIntent.captureMode as CaptureMode) ?? defaultCaptureMode ?? 'visit',
    );
    setLang((armIntent.language as SttLang) ?? defaultLanguage ?? 'en');
    setShowChange(false);
    setShowPicker(false);
    // Prefer an explicitly-passed template; else fall through to the
    // specialty default resolved below.
    setSelectedTemplate(null);
    // armIntent identity changes per open; the defaults are read once here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armIntent]);

  // Seed the template from the doctor's specialty default once it resolves,
  // unless one was already chosen.
  useEffect(() => {
    if (!open || selectedTemplate) return;
    if (defaultTemplate.template) setSelectedTemplate(defaultTemplate.template);
  }, [open, defaultTemplate.template, selectedTemplate]);

  // Consent check: only when a patient is attached. No patient → no consent.
  useEffect(() => {
    setNeedsConsent(false);
    setConsentChecked(false);
    if (!armIntent?.patientId) {
      setConsentChecked(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const consent = await audioRetentionApi.myConsent();
        if (!cancelled) setNeedsConsent(Boolean(consent.effective));
      } catch {
        // Never block clinical work on a consent-check hiccup — the separate
        // attestation modal still guards startSession as a fallback.
        if (!cancelled) setNeedsConsent(false);
      } finally {
        if (!cancelled) setConsentChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [armIntent?.patientId]);

  // Lock body scroll + ESC-to-cancel while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelArm();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, cancelArm]);

  if (!armIntent) return null;

  const patientName = isRtl
    ? armIntent.patientNameAr || armIntent.patientNameEn || ''
    : armIntent.patientNameEn || armIntent.patientNameAr || '';

  const templateName = (tpl: Template | null): string => {
    if (!tpl) return isRtl ? 'ملاحظة عامة' : 'General note';
    const n = tpl.name;
    return (isRtl ? n.ar || n.en : n.en || n.ar) || (isRtl ? 'ملاحظة' : 'Note');
  };
  const langLabel = (l: SttLang): string =>
    l === 'en'
      ? isRtl
        ? 'إنجليزي'
        : 'English'
      : l === 'ar'
        ? isRtl
          ? 'عربي'
          : 'Arabic'
        : isRtl
          ? 'تلقائي'
          : 'Auto';
  const modeLabel = (m: CaptureMode): string =>
    m === 'visit'
      ? isRtl
        ? 'زيارة'
        : 'Visit'
      : isRtl
        ? 'إملاء'
        : 'Dictation';

  const handleStart = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (selectedTemplate) rememberRecentTemplate(selectedTemplate.id);
    void startSession({
      ...armIntent,
      templateId: selectedTemplate?.id ?? armIntent.templateId,
      language: lang,
      captureMode,
      // We've shown consent inline (or it isn't needed) — don't let the
      // separate attestation modal double-prompt. Only assert this once the
      // consent check has actually resolved; otherwise fall back to the modal.
      skipAttestationGate: consentChecked,
    });
    cancelArm();
  };

  // ── Template picker sub-view ────────────────────────────────────────────
  if (showPicker) {
    return (
      <div
        className="fixed inset-0 z-[60] flex flex-col"
        style={{ backgroundColor: 'var(--color-bg-secondary)' }}
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <header
          className="flex items-center justify-between px-4 sm:px-6 py-3 border-b flex-shrink-0"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border-primary)',
          }}
        >
          <p
            className="text-sm font-semibold"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {isRtl ? 'اختر نوع الملاحظة' : 'Choose note type'}
          </p>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setShowPicker(false)}
            aria-label={t('common.close')}
          >
            <X />
          </Button>
        </header>
        <main className="flex-1 overflow-hidden">
          <TemplatePickerPhase
            onPick={(tpl) => {
              setSelectedTemplate(tpl);
              setShowPicker(false);
            }}
            onSkip={() => {
              setSelectedTemplate(null);
              setShowPicker(false);
            }}
          />
        </main>
      </div>
    );
  }

  const initials =
    patientName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('') || '·';

  return (
    <>
    <div
      className="fixed inset-0 z-[60] flex flex-col"
      style={{ backgroundColor: 'var(--color-bg-secondary)' }}
      dir={isRtl ? 'rtl' : 'ltr'}
      role="dialog"
      aria-label={isRtl ? 'بدء التسجيل' : 'Start recording'}
    >
      <header
        className="flex items-center justify-between px-4 sm:px-6 py-3 border-b flex-shrink-0"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border-primary)',
        }}
      >
        <p
          className="text-xs uppercase tracking-wide font-semibold"
          style={{ color: 'var(--color-text-tertiary)' }}
        >
          {isRtl ? 'زيارة جديدة' : 'New encounter'}
        </p>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={cancelArm}
          aria-label={t('common.close')}
        >
          <X />
        </Button>
      </header>

      <main className="flex-1 overflow-y-auto flex items-start sm:items-center justify-center px-4 py-6">
        <div className="w-full max-w-md flex flex-col gap-5">
          {/* Patient (or unassigned note) */}
          {patientName ? (
            <div
              className="flex items-center gap-3 rounded-xl p-3"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                border: '1px solid var(--color-border-primary)',
              }}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold"
                style={{
                  backgroundColor: 'var(--color-primary-100)',
                  color: 'var(--color-primary-700)',
                }}
              >
                {initials}
              </div>
              <p
                className="text-base font-semibold truncate"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {patientName}
              </p>
            </div>
          ) : (
            <p
              className="text-sm text-center"
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              {isRtl
                ? 'بدون مريض — يمكنك إرفاق مريض بعد البدء.'
                : 'No patient yet — you can attach one after you start.'}
            </p>
          )}

          {/* Start */}
          <button
            type="button"
            onClick={handleStart}
            className="w-full inline-flex items-center justify-center gap-2.5 rounded-2xl px-6 py-4 text-base font-semibold transition-transform active:scale-[0.99]"
            style={{
              backgroundColor: 'var(--color-primary-600)',
              color: 'var(--color-text-button, #fff)',
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            <span
              aria-hidden
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: 'currentColor',
              }}
            />
            {needsConsent
              ? isRtl
                ? 'موافقة وابدأ التسجيل'
                : 'I confirm — start recording'
              : isRtl
                ? 'ابدأ التسجيل'
                : 'Start recording'}
          </button>

          {/* Summary + change */}
          <div className="flex flex-col items-center gap-2">
            <p
              className="text-sm text-center"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {modeLabel(captureMode)}
              <span style={{ color: 'var(--color-text-tertiary)' }}> · </span>
              {templateName(selectedTemplate)}
              <span style={{ color: 'var(--color-text-tertiary)' }}> · </span>
              {langLabel(lang)}
            </p>
            <button
              type="button"
              onClick={() => setShowChange((v) => !v)}
              className="inline-flex items-center gap-1 text-sm font-semibold"
              style={{ color: 'var(--color-primary-600)' }}
              aria-expanded={showChange}
            >
              {isRtl ? 'تغيير' : 'Change'}
              <ChevronDown
                className="w-4 h-4 transition-transform"
                style={{ transform: showChange ? 'rotate(180deg)' : 'none' }}
              />
            </button>
            <p
              className="text-xs text-center"
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              {isRtl
                ? 'لا يتم تسجيل أي شيء حتى تضغط ابدأ.'
                : 'Nothing is recorded until you press Start.'}
            </p>
          </div>

          {/* Sovereign-AI reassurance — always, so the screen reads complete
              and the doctor sees where the audio goes. */}
          <div
            className="flex items-start gap-3 rounded-xl p-3"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              border: '1px solid var(--color-border-primary)',
            }}
          >
            <ShieldCheck
              className="w-4 h-4 mt-0.5 flex-shrink-0"
              style={{ color: 'var(--color-primary-700)' }}
              aria-hidden
            />
            <p
              className="text-xs leading-relaxed"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {isRtl
                ? 'ذكاء اصطناعي سيادي — يعمل على خوادمك، وبيانات المريض لا تغادرها. سيظهر النص مباشرةً أثناء حديثك.'
                : 'Sovereign AI — runs on your servers, patient data never leaves them. Transcription appears live as you speak.'}
            </p>
          </div>

          {/* Consent — only when a patient is attached AND retention is on.
              Folds the old attestation modal inline: Start = confirmation. */}
          {needsConsent && (
            <div className="flex flex-col gap-3">
              <div
                className="rounded-xl p-3 text-sm leading-relaxed"
                style={{
                  backgroundColor: 'var(--color-bg-primary)',
                  border: '1px solid var(--color-border-primary)',
                  color: 'var(--color-text-primary)',
                }}
              >
                {isRtl
                  ? '"أؤكد أنني أبلغت المريض وحصلت على موافقته على تسجيل هذه الزيارة واستخدامها لتحسين الذكاء الاصطناعي." الضغط على ابدأ يُعدّ تأكيداً.'
                  : '"I confirm I have informed the patient and obtained consent to record this encounter and use it to improve our AI." Pressing Start confirms this.'}
              </div>
              <Button
                variant="link"
                size="xs"
                className="self-center"
                onClick={() => setDisclosureOpen(true)}
              >
                <FileText />
                {isRtl
                  ? 'إظهار إفصاح الخصوصية للمريض'
                  : 'Show the patient disclosure'}
              </Button>
            </div>
          )}

          {/* Change sheet — per-encounter overrides */}
          {showChange && (
            <div
              className="flex flex-col gap-4 rounded-xl p-4"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                border: '1px solid var(--color-border-primary)',
              }}
            >
              {/* Mode */}
              <div className="flex flex-col gap-2">
                <span
                  className="text-[11px] uppercase tracking-wider font-bold"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  {isRtl ? 'ماذا تفعل؟' : 'What are you doing?'}
                </span>
                <div
                  role="radiogroup"
                  aria-label={isRtl ? 'وضع التسجيل' : 'Capture mode'}
                  className="flex gap-2"
                >
                  {(
                    [
                      {
                        value: 'visit' as const,
                        Icon: Users,
                        title: isRtl ? 'تسجيل الزيارة' : 'Record visit',
                        sub: isRtl ? 'أنت + المريض' : 'You + patient',
                      },
                      {
                        value: 'dictate' as const,
                        Icon: Mic,
                        title: isRtl ? 'إملاء ملاحظات' : 'Dictate notes',
                        sub: isRtl ? 'أنت فقط' : 'Just you',
                      },
                    ] satisfies Array<{
                      value: CaptureMode;
                      Icon: typeof Users;
                      title: string;
                      sub: string;
                    }>
                  ).map((o) => {
                    const active = captureMode === o.value;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setCaptureMode(o.value)}
                        className="flex-1 rounded-xl px-3 py-2.5 text-start transition-colors"
                        style={{
                          backgroundColor: active
                            ? 'var(--color-bg-selected)'
                            : 'var(--color-bg-secondary)',
                          border: `1.5px solid ${
                            active
                              ? 'var(--color-primary-500)'
                              : 'var(--color-border-primary)'
                          }`,
                          color: 'var(--color-text-primary)',
                        }}
                      >
                        <span className="flex items-center gap-1.5 text-sm font-semibold">
                          <o.Icon className="w-3.5 h-3.5" aria-hidden />
                          {o.title}
                        </span>
                        <span
                          className="block text-xs mt-0.5"
                          style={{ color: 'var(--color-text-secondary)' }}
                        >
                          {o.sub}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Note type */}
              <div className="flex flex-col gap-2">
                <span
                  className="text-[11px] uppercase tracking-wider font-bold"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  {isRtl ? 'نوع الملاحظة' : 'Note type'}
                </span>
                <button
                  type="button"
                  onClick={() => setShowPicker(true)}
                  className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border-primary)',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {templateName(selectedTemplate)}
                  <span style={{ color: 'var(--color-text-tertiary)' }}>
                    {isRtl ? 'تغيير' : 'Change'}
                  </span>
                </button>
              </div>

              {/* Language */}
              <div className="flex flex-col gap-2">
                <span
                  className="text-[11px] uppercase tracking-wider font-bold"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  {isRtl ? 'لغة الكلام' : 'Speaking language'}
                </span>
                <div
                  role="radiogroup"
                  aria-label={isRtl ? 'لغة الكلام' : 'Speaking language'}
                  className="flex gap-2"
                >
                  {(['en', 'ar', 'auto'] satisfies SttLang[]).map((l) => {
                    const active = lang === l;
                    return (
                      <button
                        key={l}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setLang(l)}
                        className="flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors"
                        style={{
                          backgroundColor: active
                            ? 'var(--color-bg-selected)'
                            : 'var(--color-bg-secondary)',
                          border: `1.5px solid ${
                            active
                              ? 'var(--color-primary-500)'
                              : 'var(--color-border-primary)'
                          }`,
                          color: 'var(--color-text-primary)',
                        }}
                      >
                        {langLabel(l)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
    <PatientDisclosureModal
      open={disclosureOpen}
      onClose={() => setDisclosureOpen(false)}
    />
    </>
  );
};

export default EncounterArmPanel;
